import { usd, num } from "@/lib/format";

/** Seller-count competition bands — matches backend seller_competition_band. */
export function sellerCompetitionBand(sellerCount) {
  const n = Number(sellerCount);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n <= 3) return "Low";
  if (n <= 9) return "Moderate";
  return "High";
}

/** Featured Offer Competition cell — never a historical Highest value. */
export function featuredOfferCompetitionLabel(sellerCount, additionalStartingPrice) {
  const n = Number(sellerCount);
  if (!Number.isFinite(n) || n <= 0) return null;
  let label = `${n} sellers`;
  if (additionalStartingPrice == null || additionalStartingPrice === "") {
    return label;
  }
  const price = Number(additionalStartingPrice);
  if (Number.isFinite(price) && price >= 0) {
    label = `${label} · additional from ${usd(price)}`;
  }
  return label;
}

/**
 * Normalize Current / Range / Competition rows for the overview table.
 * Never invent historical Average / Lowest / Highest.
 * Additional-offer starting price belongs in Featured Offer Competition only.
 */
export function buildCurrentRangeCompetitionRows(bundle, product) {
  const ca = bundle?.current_average;
  const competition = product?.competition || {};
  // Prefer authoritative aggregate seller_count — never offers.length.
  const sellerCount =
    ca?.seller_count
    ?? competition.seller_count
    ?? bundle?.seller_count
    ?? null;
  const band = sellerCompetitionBand(sellerCount);
  const starting =
    ca?.additional_offers_starting_price
    ?? competition.additional_offers_starting_price
    ?? bundle?.additional_offers_starting_price
    ?? null;
  const featuredCompetition = featuredOfferCompetitionLabel(sellerCount, starting);

  const rawRows = Array.isArray(ca?.rows) ? ca.rows : [];
  if (!rawRows.length) return [];

  const out = [];
  for (const r of rawRows) {
    if (!r || typeof r !== "object") continue;
    const metricRaw = String(r.metric || "");
    const metricLower = metricRaw.toLowerCase();
    if (metricLower === "new offer count" || metricLower === "list price") continue;

    const current = r.current != null ? r.current : (Array.isArray(r.values) ? r.values[0] : null);
    let metric = metricRaw;
    if (metricLower === "new (lowest)" || metricLower === "new lowest") {
      metric = "New lowest price";
    }

    let competitionCell = null;

    if (metric === "Featured Offer price") {
      competitionCell = featuredCompetition;
    } else if (metric === "New lowest price") {
      competitionCell = band ?? null;
    } else if (metric === "Seller count") {
      competitionCell = band ?? null;
    } else {
      competitionCell = null;
    }

    out.push({
      metric,
      unit: r.unit || (metric === "Rating" ? "float" : metric.includes("price") || metric.includes("Price") ? "money" : "int"),
      current,
      average: null,
      lowest: null,
      // Historical highest is never populated from additional-offer starting price.
      highest: null,
      highestKind: null,
      competition: competitionCell,
      provenance: r.provenance,
    });
  }
  return out;
}

export function formatCrcCurrent(value, unit) {
  if (value == null || value === "") return "—";
  if (unit === "money") return usd(value);
  if (unit === "float") return Number(value).toFixed(1);
  if (unit === "int") return num(Math.round(Number(value)));
  return typeof value === "number" ? num(value) : String(value);
}

export function formatCrcHighest(row) {
  // Historical highest only — additional-offer start never appears here.
  if (row?.highest == null || row?.highestKind === "additional_offer_start") return "—";
  return formatCrcCurrent(row.highest, row.unit);
}

export function normalizePurchaseActivityBadge(raw) {
  const cleaned = String(raw || "")
    .replace(/,\s*try a subscription\b.*$/i, "")
    .trim();
  return cleaned || null;
}
